import { useState } from 'react';

const DailyEntryForm = ({ existingEntry, moodOptions, painLevels, symptomOptions, onSubmit, onCancel }) => {
  const [mood, setMood] = useState(existingEntry?.mood || '');
  const [painLevel, setPainLevel] = useState(existingEntry?.painLevel || '');
  const [symptoms, setSymptoms] = useState(existingEntry?.symptoms || []);
  const [notes, setNotes] = useState(existingEntry?.notes || '');
  const [flow, setFlow] = useState(existingEntry?.flow || '');
  const [energy, setEnergy] = useState(existingEntry?.energy || 3);

  const flowOptions = [
    { value: 'none', label: 'None', emoji: '○' },
    { value: 'spotting', label: 'Spotting', emoji: '◐' },
    { value: 'light', label: 'Light', emoji: '●' },
    { value: 'medium', label: 'Medium', emoji: '●●' },
    { value: 'heavy', label: 'Heavy', emoji: '●●●' }
  ];

  const handleSymptomToggle = (symptom) => {
    if (symptoms.includes(symptom)) {
      setSymptoms(prev => prev.filter(s => s !== symptom));
    } else {
      setSymptoms(prev => [...prev, symptom]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ 
      mood, 
      painLevel, 
      symptoms, 
      notes,
      flow,
      energy
    });
  };

  return (
    <form onSubmit={handleSubmit} className="cs-daily-form">
      {/* Mood Selection */}
      <div className="cs-form-section">
        <label className="cs-form-label">How are you feeling?</label>
        <div className="cs-mood-selector">
          {moodOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`cs-mood-option ${mood === option.value ? 'selected' : ''}`}
              onClick={() => setMood(option.value)}
              style={mood === option.value ? { 
                backgroundColor: `${option.color}30`,
                borderColor: option.color 
              } : {}}
            >
              <span 
                className="cs-mood-indicator"
                style={{ backgroundColor: option.color }}
              ></span>
              <span className="cs-mood-text">{option.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Flow Level */}
      <div className="cs-form-section">
        <label className="cs-form-label">Flow</label>
        <div className="cs-flow-selector">
          {flowOptions.map(option => (
            <button
              key={option.value}
              type="button"
              className={`cs-flow-option ${flow === option.value ? 'selected' : ''}`}
              onClick={() => setFlow(option.value)}
            >
              <div className="cs-flow-dots">
                {Array.from({ length: ['none', 'spotting', 'light', 'medium', 'heavy'].indexOf(option.value) }).map((_, i) => (
                  <span key={i} className="cs-flow-dot"></span>
                ))}
              </div>
              <span className="cs-flow-label">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Pain Level */}
      <div className="cs-form-section">
        <label className="cs-form-label">Pain Level</label>
        <div className="cs-pain-selector">
          {painLevels.map(option => (
            <button
              key={option.value}
              type="button"
              className={`cs-pain-option ${painLevel === option.value ? 'selected' : ''}`}
              onClick={() => setPainLevel(option.value)}
              data-level={option.level}
            >
              <div className="cs-pain-level-indicator" data-level={option.level}>
                {Array.from({ length: option.level + 1 }).map((_, i) => (
                  <span key={i} className="cs-pain-bar"></span>
                ))}
              </div>
              <span className="cs-pain-text">{option.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Energy Level */}
      <div className="cs-form-section">
        <label className="cs-form-label">Energy Level</label>
        <div className="cs-energy-selector">
          <input
            type="range"
            min="1"
            max="5"
            value={energy}
            onChange={(e) => setEnergy(parseInt(e.target.value))}
            className="cs-energy-slider"
          />
          <div className="cs-energy-labels">
            <span>Low</span>
            <div className="cs-energy-dots">
              {[1, 2, 3, 4, 5].map(level => (
                <span 
                  key={level} 
                  className={`cs-energy-dot ${energy >= level ? 'active' : ''}`}
                ></span>
              ))}
            </div>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Symptoms */}
      <div className="cs-form-section">
        <label className="cs-form-label">Symptoms</label>
        <div className="cs-symptoms-grid">
          {symptomOptions.map(symptom => (
            <button
              key={symptom.value}
              type="button"
              className={`cs-symptom-chip ${symptoms.includes(symptom.value) ? 'selected' : ''}`}
              onClick={() => handleSymptomToggle(symptom.value)}
            >
              <span className="cs-symptom-indicator"></span>
              <span className="cs-symptom-text">{symptom.value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="cs-form-section">
        <label className="cs-form-label">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How was your day? Any thoughts to capture..."
          className="cs-notes-input"
          rows={3}
        />
      </div>

      {/* Actions */}
      <div className="cs-form-actions">
        <button type="button" onClick={onCancel} className="cs-btn-cancel">
          Cancel
        </button>
        <button type="submit" className="cs-btn-save">
          <i className="pi pi-check"></i>
          Save Entry
        </button>
      </div>
    </form>
  );
};

export default DailyEntryForm; 